import React, { useEffect, useState } from 'react';
// IndexedDb
import { useLiveQuery } from 'dexie-react-hooks';
import { dexieDB } from '../../shared/db';
//Models
import { ProductInterface, CategoryInterface, ChildVariation, TagInterface, ExtraAttributes, BrandInterface } from '../../shared/types';
import { Button, Col, Row, Table } from 'react-bootstrap';
//API
import { getWebshopProductBrands, getWebshopProductCategories, getWebshopProductTags } from 'Api';
//Components
import { SimpleEditProducts, VariableEditProducts, SimpleProductModal } from 'components/products';
import { useSelectedProducts } from 'contexts/selected-products';
import CustomToast from 'components/toast';
import Search from 'components/search';

export const EditProductsPage: React.FC = () => {
  const [editedProducts, setEditedProducts] = useState<ProductInterface[]>([]);
  const [isSimpleModalOpen, setIsSimpleModalOpen] = useState(false);
  const [productUnderEdit, setProductUnderEdit] = useState<ProductInterface | null>();
  const [webshopCategories, setWebshopCategories] = useState<CategoryInterface[]>([]);
  const [webshopTags, setWebshopTags] = useState<TagInterface[]>([]);
  const [filteredTags, setFilteredTags] = useState<TagInterface[]>([]);
  const [sortField, setSortField] = useState<keyof ProductInterface | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [categorySearchTerm, setCategorySearchTerm] = useState<string>('');
  const [tagSearchTerm, setTagSearchTerm] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [currentPage] = useState<number>(1);
  const [resultsPerPage] = useState<number>(20);
  const { selectedProducts, setSelectedProducts } = useSelectedProducts();
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isLoadingTags, setIsLoadingTags] = useState(true);
  const [visibleCount, setVisibleCount] = useState<number>(20);
  const [massedit, setMassedit] = useState<boolean>(false);
  const [markAll, setMarkAll] = useState<boolean>(false);
  const [editedCount, setEditedCount] = useState<number>(0);
  const [categoryTree, setCategoryTree] = useState<CategoryInterface[]>([]);

  const [vejlPrice, setVejlPrice] = useState<string>('');
  const [discountPrice, setDiscountPrice] = useState<string>('');
  const [costPrice, setCostPrice] = useState<string>('');
  const [discount, setDiscount] = useState<string>('');
  const [discountType, setDiscountType] = useState<string>('');
  const [discountCost, setDiscountCost] = useState<string>('');
  const [discountCostType, setDiscountCostType] = useState<string>('');
  const [discountVejl, setDiscountVejl] = useState<string>('');
  const [discountVejlType, setDiscountVejlType] = useState<string>('');
  const [stock, setStock] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [attributes, setAttributes] = useState<ExtraAttributes[]>([]);
  const [categorySelected, setCategorySelected] = useState<CategoryInterface[]>([]);
  const [tagSelected, setTagSelected] = useState<CategoryInterface[]>([]);
  const [categoryShared, setCategoryShared] = useState<CategoryInterface[]>([]);
  const [tagShared, setTagShared] = useState<TagInterface[]>([]);

  // brand state
  const [webshopBrands, setWebshopBrands] = useState<BrandInterface[]>([]);
  const [brandSearchTerm, setBrandSearchTerm] = useState<string>('');
  const [brandTree, setBrandTree] = useState<BrandInterface[]>([]);
  const [brandSelected, setBrandSelected] = useState<BrandInterface[]>([]);
  const [brandShared, setBrandShared] = useState<BrandInterface[]>([]);
  const [isLoadingBrands, setIsLoadingBrands] = useState<boolean>(true);

  const [toastActive, setToastActive] = useState(false);

  const editedProductsCount = useLiveQuery(() => dexieDB.editedProducts.count());
  const allEditedProducts = useLiveQuery(() => {
    return dexieDB.editedProducts.toArray().then((products) => {
      if (sortField === 'vendor_products_sku') {
        products.sort((a, b) => {
          const skuA = a.product_variations?.[0]?.vendor_product_variations_sku ?? a.vendor_products_sku;
          const skuB = b.product_variations?.[0]?.vendor_product_variations_sku ?? b.vendor_products_sku;

          if (sortOrder === 'asc') {
            return skuA.localeCompare(skuB);
          } else {
            return skuB.localeCompare(skuA);
          }
        });
      } else if (sortField === 'vendor_products_price') {
        products.sort((a, b) => {
          let priceA;
          let priceB;

          // Sortere på mindste prisen af alle varianter i et produkt
          if (a.product_variations && a.product_variations?.length > 0) {
            const pricesA = a.product_variations.map((variation) => {
              return variation.vendor_product_variations_price;
            });
            priceA = Math.min(...pricesA);
          }

          if (b.product_variations && b.product_variations?.length > 0) {
            const pricesB = b.product_variations.map((variation) => {
              return variation.vendor_product_variations_price;
            });
            priceB = Math.min(...pricesB);
          }

          priceA ? priceA : (priceA = a.vendor_products_price);
          priceB ? priceB : (priceB = b.vendor_products_price);

          if (sortOrder === 'asc') {
            return priceA - priceB;
          } else {
            return priceB - priceA;
          }
        });
      } else if (sortField) {
        products.sort((a, b) => {
          if (sortOrder === 'asc') {
            return String(a[sortField]).localeCompare(String(b[sortField]));
          } else {
            return String(b[sortField]).localeCompare(String(a[sortField]));
          }
        });
      }

      return products;
    });
  }, [sortField, sortOrder]);

  const handleSort = (field: string) => {
    if (field === sortField) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field as keyof ProductInterface);
      setSortOrder('asc');
    }

    localStorage.setItem('editSortField', field);
    localStorage.setItem('editSortOrder', sortOrder === 'asc' ? 'desc' : 'asc');
  };

  useEffect(() => {
    const savedSortField = localStorage.getItem('editSortField');
    const savedSortOrder = localStorage.getItem('editSortOrder');

    if (savedSortField) {
      setSortField(savedSortField as keyof ProductInterface);
    }

    if (savedSortOrder) {
      setSortOrder(savedSortOrder as 'asc' | 'desc');
    }
  }, []);

  useEffect(() => {
    if (!allEditedProducts) {
      return;
    }

    setEditedProducts(allEditedProducts);
  }, [allEditedProducts, currentPage, resultsPerPage]);

  useEffect(() => {
    let isMounted = true;

    const getWebshopProductCategoriesHandler = async (): Promise<void> => {
      setIsLoadingCategories(true);
      const response = await getWebshopProductCategories();

      if (isMounted) {
        if (response) {
          setWebshopCategories(response.data);
        }
      }

      setIsLoadingCategories(false);
    };

    const getWebshopProductBrandsHandler = async (): Promise<void> => {
      setIsLoadingBrands(true);
      const response = await getWebshopProductBrands();

      if (isMounted && response) {
        setWebshopBrands(response.data);
      }
      setIsLoadingBrands(false);
    };

    const getWebshopProductTagsHandler = async (): Promise<void> => {
      setIsLoadingTags(true);
      const response = await getWebshopProductTags();
      if (isMounted) {
        if (response) {
          setWebshopTags(response.data);
        }
      }

      setIsLoadingTags(false);
    };

    getWebshopProductCategoriesHandler();
    getWebshopProductTagsHandler();
    getWebshopProductBrandsHandler();

    return () => {
      isMounted = false;
    };
  }, []);

  // Håndter kategori på masseredigering
  useEffect(() => {
    setCategoryTree(categoryNesting(webshopCategories));

    if (categorySearchTerm) {
      const matches = webshopCategories.filter((c) => c.name?.toLowerCase().includes(categorySearchTerm.toLowerCase()));
      setCategoryTree(matches);
    }
  }, [webshopCategories, categorySearchTerm]);

  useEffect(() => {
    setBrandTree(brandNesting(webshopBrands));

    if (brandSearchTerm) {
      const matches = webshopBrands.filter((b) => b.name?.toLowerCase().includes(brandSearchTerm.toLowerCase()));
      setBrandTree(matches);
    }
  }, [webshopBrands, brandSearchTerm]);

  useEffect(() => {
    setFilteredTags(webshopTags);

    if (tagSearchTerm) {
      const matchesSearchTerm = webshopTags.filter((tag) => {
        return tag.name?.toLowerCase().includes(tagSearchTerm.toLowerCase());
      });

      setFilteredTags(matchesSearchTerm);
    }
  }, [webshopTags, tagSearchTerm]);

  // Filtrerer både på kategori og søgning
  useEffect(() => {
    if (!allEditedProducts) return;

    const filteredProducts = allEditedProducts.filter((product) => {
      const matchesSearchTerm = searchTerm
        ? product.vendor_products_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.vendor_products_sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.vendor_products_ean?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.vendor_products_price?.toString().includes(searchTerm.toLowerCase())
        : true;

      const matchesCategory = category
        ? Array.isArray(product.vendor_products_category)
          ? product.vendor_products_category.some((cat) => {
              if (cat && typeof cat === 'object' && cat.id) {
                return parseInt(`${cat.id}`) === parseInt(`${category}`);
              }

              return false;
            })
          : product.vendor_products_category
            ? product.vendor_products_category.includes(category)
            : false
        : true;

      return matchesSearchTerm && matchesCategory;
    });

    // Opdaterer state med de produkter, som skal vises på den nuværende side
    setEditedProducts(filteredProducts);
  }, [allEditedProducts, currentPage, resultsPerPage, searchTerm, category]);

  useEffect(() => {
    if (selectedProducts) {
      const categoryCount: Record<string, { category: CategoryInterface; count: number }> = {};

      selectedProducts.forEach((product) => {
        if (Array.isArray(product.vendor_products_category)) {
          (product.vendor_products_category as CategoryInterface[]).forEach((category) => {
            if (categoryCount[category.id]) {
              categoryCount[category.id].count += 1;
            } else {
              categoryCount[category.id] = { category, count: 1 };
            }
          });
        }
      });

      const sharedCategories = Object.values(categoryCount)
        .filter((entry) => entry.count === selectedProducts.length)
        .map((entry) => entry.category);
      setCategoryShared(sharedCategories);

      const selectedCategories = Object.values(categoryCount).map((entry) => entry.category);
      setCategorySelected(selectedCategories);

      const brandCount: Record<string, { brand: CategoryInterface; count: number }> = {};

      selectedProducts.forEach((prod) => {
        if (Array.isArray(prod.vendor_products_brand)) {
          (prod.vendor_products_brand as CategoryInterface[]).forEach((b) => {
            brandCount[b.id] ? (brandCount[b.id].count += 1) : (brandCount[b.id] = { brand: b, count: 1 });
          });
        }
      });

      setBrandShared(
        Object.values(brandCount)
          .filter((e) => e.count === selectedProducts.length)
          .map((e) => e.brand),
      );

      setBrandSelected(Object.values(brandCount).map((e) => e.brand));

      const tagCount: Record<string, { tag: TagInterface; count: number }> = {};

      selectedProducts.forEach((product) => {
        if (Array.isArray(product.vendor_products_tags)) {
          (product.vendor_products_tags as TagInterface[]).forEach((tag) => {
            if (tagCount[tag.id]) {
              tagCount[tag.id].count += 1;
            } else {
              tagCount[tag.id] = { tag, count: 1 };
            }
          });
        }
      });

      const sharedTags = Object.values(tagCount)
        .filter((entry) => entry.count === selectedProducts.length)
        .map((entry) => entry.tag);
      setTagShared(sharedTags);

      const selectedTag = Object.values(tagCount).map((entry) => entry.tag);
      setTagSelected(selectedTag);
    }
  }, [selectedProducts]);

  function clearAll() {
    setVejlPrice('');
    setDiscountVejl('');
    setDiscountVejlType('');
    setDiscountPrice('');
    setDiscount('');
    setDiscountType('');
    setCostPrice('');
    setDiscountCost('');
    setDiscountCostType('');
    setStock('');
    setStatus('');
    setSearchTerm('');
    setCategorySearchTerm('');
    setCategory('');
    setCategorySelected([]);
    setAttributes([]);
    setSelectedProducts([]);
    setMarkAll(false);
  }

  const handleAddAttribute = () => {
    const newAttribute = { name: '', value: '' };
    setAttributes((currentAttributes) => [...currentAttributes, newAttribute]);
  };

  const handleRemoveAttribute = (index: number) => {
    setAttributes((currentAttributes) => currentAttributes.filter((_, currentIndex) => currentIndex !== index));
  };

  const handleAttributeChange = (index: number, key: keyof ExtraAttributes, value: string) => {
    setAttributes((currentAttributes) => currentAttributes.map((attr, i) => (i === index ? { ...attr, [key]: value } : attr)));
  };

  function handleCategoryChange(event: React.ChangeEvent<HTMLSelectElement>) {
    setCategory(event.target.value);
  }

  const handleCategorySelected = (category: CategoryInterface) => {
    setSelectedProducts((prevSelectedProducts) => {
      const isCategoryShared = prevSelectedProducts.every(
        (product) =>
          Array.isArray(product.vendor_products_category) &&
          (product.vendor_products_category as CategoryInterface[]).some((productCategory) => productCategory.id === category.id),
      );

      const isCategoryPartial =
        prevSelectedProducts.some(
          (product) =>
            Array.isArray(product.vendor_products_category) &&
            (product.vendor_products_category as CategoryInterface[]).some((productCategory) => productCategory.id === category.id),
        ) && !isCategoryShared;

      const updatedProducts = prevSelectedProducts.map((product) => {
        let categories = product.vendor_products_category;

        if (!Array.isArray(categories)) {
          categories = typeof categories === 'string' ? [categories] : [];
        }

        let updatedCategories = (categories as CategoryInterface[]).filter((productCategory) =>
          webshopCategories.some((webshopCategory) => webshopCategory.id === productCategory.id),
        );

        updatedCategories =
          isCategoryShared || isCategoryPartial
            ? updatedCategories.filter((productCategory) => productCategory.id !== category.id)
            : [...updatedCategories, category];

        return {
          ...product,
          vendor_products_category: updatedCategories,
        };
      });

      if (isCategoryShared || isCategoryPartial) {
        setCategorySelected((prev) => prev.filter((selectedCategory) => selectedCategory.id !== category.id));
      } else {
        setCategorySelected((prev) => [...prev, category]);
      }

      return updatedProducts;
    });
  };

  const handleBrandSelected = (brand: CategoryInterface) => {
    setSelectedProducts((prevSelectedProducts) => {
      const isBrandShared = prevSelectedProducts.every(
        (product) =>
          Array.isArray(product.vendor_products_brand) &&
          (product.vendor_products_brand as CategoryInterface[]).some((productBrand) => productBrand.id === brand.id),
      );

      const isBrandPartial =
        prevSelectedProducts.some(
          (product) =>
            Array.isArray(product.vendor_products_brand) &&
            (product.vendor_products_brand as CategoryInterface[]).some((productBrand) => productBrand.id === brand.id),
        ) && !isBrandShared;

      const updatedProducts = prevSelectedProducts.map((product) => {
        let brands = product.vendor_products_brand;

        if (!Array.isArray(brands)) {
          brands = typeof brands === 'string' ? [brands] : [];
        }

        let updatedBrands = (brands as CategoryInterface[]).filter((productBrand) => webshopBrands.some((webshopBrand) => webshopBrand.id === productBrand.id));

        updatedBrands = isBrandShared || isBrandPartial ? updatedBrands.filter((productBrand) => productBrand.id !== brand.id) : [...updatedBrands, brand];

        return {
          ...product,
          vendor_products_brand: updatedBrands,
        };
      });

      if (isBrandShared || isBrandPartial) {
        setBrandSelected((prev) => prev.filter((selectedBrand) => selectedBrand.id !== brand.id));
      } else {
        setBrandSelected((prev) => [...prev, brand]);
      }

      return updatedProducts;
    });
  };

  const handleTagSelected = (tag: TagInterface) => {
    setSelectedProducts((prevSelectedProducts) => {
      const isTagShared = prevSelectedProducts.every(
        (product) =>
          Array.isArray(product.vendor_products_tags) && (product.vendor_products_tags as TagInterface[]).some((productTag) => productTag.id === tag.id),
      );

      const isTagPartial =
        prevSelectedProducts.some(
          (product) =>
            Array.isArray(product.vendor_products_tags) && (product.vendor_products_tags as TagInterface[]).some((productTag) => productTag.id === tag.id),
        ) && !isTagShared;

      const updatedProducts = prevSelectedProducts.map((product) => {
        let tags = product.vendor_products_tags;

        if (!Array.isArray(tags)) {
          tags = typeof tags === 'string' ? [tags] : [];
        }

        let updatedTags = (tags as TagInterface[]).filter((productTag) => webshopTags.some((webshopTag) => webshopTag.id === productTag.id));

        updatedTags = isTagShared || isTagPartial ? updatedTags.filter((productTag) => productTag.id !== tag.id) : [...updatedTags, tag];

        return {
          ...product,
          vendor_products_tags: updatedTags,
        };
      });

      if (isTagShared || isTagPartial) {
        setTagSelected((prev) => prev.filter((selectedTag) => selectedTag.id !== tag.id));
      } else {
        setTagSelected((prev) => [...prev, tag]);
      }

      return updatedProducts;
    });
  };

  const categoryNesting = (categories: CategoryInterface[]) => {
    const categoryMap: { [key: string]: CategoryInterface } = {};

    categories.forEach((category) => {
      categoryMap[category.id] = { ...category, children: [] };
    });

    const tree: CategoryInterface[] = [];

    categories.forEach((category) => {
      if (category.parent === 0) {
        tree.push(categoryMap[category.id]);
      } else if (category.parent && categoryMap[category.parent]) {
        (categoryMap[category.parent].children as CategoryInterface[]).push(categoryMap[category.id]);
      }
    });
    return tree;
  };

  const brandNesting = (brands: CategoryInterface[]) => {
    const brandMap: Record<string, CategoryInterface & { children: CategoryInterface[] }> = {};
    brands.forEach((b) => (brandMap[b.id] = { ...b, children: [] }));

    const tree: CategoryInterface[] = [];
    brands.forEach((brand) => {
      if (brand.parent === 0) {
        tree.push(brandMap[brand.id]);
      } else if (brand.parent && brandMap[brand.parent]) {
        (brandMap[brand.parent].children as CategoryInterface[]).push(brandMap[brand.id]);
      }
    });
    return tree;
  };

  const categoryView = () => {
    const renderCategoryView = (category: CategoryInterface) => {
      const isCategoryShared = categoryShared.some((sharedCategory) => sharedCategory.id === category.id);
      const isCategoryPartial = categorySelected.some((selectedCategory) => selectedCategory.id === category.id) && !isCategoryShared;
      let categoryParent;

      if (categorySearchTerm) {
        categoryParent = webshopCategories.find((parent) => Number(parent.id) === category.parent);
      }
      let iconSrc = '';
      if (isCategoryShared) {
        iconSrc = 'check';
      } else if (isCategoryPartial) {
        iconSrc = 'minus';
      }
      return (
        <React.Fragment key={category.id}>
          <div className="edit-custom-checkbox">
            <label>
              <input type="checkbox" value={category.id} checked={isCategoryShared} onChange={() => handleCategorySelected(category)} />
              <span className="edit-custom-checkbox-check" aria-hidden="true">
                {iconSrc && <i className={'fas fa-' + iconSrc} />}
              </span>
            </label>
            <span className="edit-custom-checkbox-text">
              {category.name}
              {categoryParent && <span>{` /${categoryParent.name}`}</span>}
            </span>
          </div>
          {category.children && category.children.length > 0 && (
            <div className="child-categories ps-3">{category.children.map((childCategory) => renderCategoryView(childCategory))}</div>
          )}
        </React.Fragment>
      );
    };

    return <div>{categoryTree.map((category) => renderCategoryView(category))}</div>;
  };

  const brandView = () => {
    const render = (brand: CategoryInterface) => {
      const isShared = brandShared.some((b) => b.id === brand.id);
      const isPartial = brandSelected.some((b) => b.id === brand.id) && !isShared;
      const icon = isShared ? 'check' : isPartial ? 'minus' : '';
      return (
        <React.Fragment key={brand.id}>
          <div className="edit-custom-checkbox">
            <label>
              <input type="checkbox" checked={isShared} onChange={() => handleBrandSelected(brand)} />
              <span className="edit-custom-checkbox-check">{icon && <i className={`fas fa-${icon}`} />}</span>
            </label>
            <span className="edit-custom-checkbox-text">{brand.name}</span>
          </div>
          {brand.children && brand.children?.length > 0 && <div className="ps-3">{brand.children.map((child) => render(child))}</div>}
        </React.Fragment>
      );
    };
    return <div>{brandTree.map((b) => render(b))}</div>;
  };

  const tagView = () => {
    const renderTagView = (tag: TagInterface) => {
      const isTagShared = tagShared.some((sharedTag) => sharedTag.id === tag.id);
      const isTagPartial = tagSelected.some((selectedTag) => selectedTag.id === tag.id) && !isTagShared;

      let iconSrc = '';
      if (isTagShared) {
        iconSrc = 'check';
      } else if (isTagPartial) {
        iconSrc = 'minus';
      }
      return (
        <React.Fragment key={tag.id}>
          <div className="edit-custom-checkbox">
            <label>
              <input type="checkbox" value={tag.id} checked={isTagShared} onChange={() => handleTagSelected(tag)} />
              <span className="edit-custom-checkbox-check" aria-hidden="true">
                {iconSrc && <i className={'fas fa-' + iconSrc} />}
              </span>
            </label>
            <span className="edit-custom-checkbox-text">{tag.name}</span>
          </div>
        </React.Fragment>
      );
    };

    return <div>{filteredTags.map((tag) => renderTagView(tag))}</div>;
  };

  const CategoryOptions = () => {
    const renderCategoryOptions = (category: CategoryInterface, depth = 0) => {
      const prefix = '-'.repeat(depth);

      return (
        <React.Fragment key={category.id}>
          <option value={category.id}>
            {prefix}
            {category.name}
          </option>
          {category.children && category.children.length > 0 && category.children.map((childCategory) => renderCategoryOptions(childCategory, depth + 1))}
        </React.Fragment>
      );
    };

    return (
      <>
        <option value="">Alle kategorier</option>
        {categoryTree.map((category) => renderCategoryOptions(category))}
      </>
    );
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleBrandSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBrandSearchTerm(e.target.value);
  };

  const handleCategorySearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCategorySearchTerm(event.target.value);
  };

  const handleTagSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTagSearchTerm(event.target.value);
  };

  const handleMarkAll = () => {
    if (!markAll) {
      setMarkAll(true);
      setSelectedProducts(editedProducts);
    } else {
      setMarkAll(false);
      setSelectedProducts([]);
    }
  };

  const activateToast = () => {
    setToastActive(true);
    setTimeout(() => {
      setToastActive(false);
    }, 4000);
  };

  const performMassChange = async () => {
    if (!selectedProducts.length) {
      return;
    }

    // === find common categories & brands ===
    const commonCategories = categorySelected.filter((cat) =>
      selectedProducts.every(
        (p) => Array.isArray(p.vendor_products_category) && (p.vendor_products_category as CategoryInterface[]).some((c) => c.id === cat.id),
      ),
    );
    const commonBrands = brandSelected.filter((br) =>
      selectedProducts.every((p) => Array.isArray(p.vendor_products_brand) && (p.vendor_products_brand as BrandInterface[]).some((b) => b.id === br.id)),
    );

    const updatedProducts = selectedProducts.map((product) => {
      const updatedProduct: ProductInterface = { ...product };

      // === CATEGORIES ===
      {
        const existing = Array.isArray(product.vendor_products_category) ? [...(product.vendor_products_category as CategoryInterface[])] : [];
        updatedProduct.vendor_products_category = [...existing, ...commonCategories.filter((c) => !existing.some((e) => e.id === c.id))];
      }

      // === BRANDS ===
      {
        const existing = Array.isArray(product.vendor_products_brand) ? [...(product.vendor_products_brand as BrandInterface[])] : [];
        updatedProduct.vendor_products_brand = [...existing, ...commonBrands.filter((b) => !existing.some((e) => e.id === b.id))];
      }

      // === STATUS ===
      updatedProduct.vendor_products_status = status || product.vendor_products_status;

      // === ATTRIBUTES ===
      {
        const base: ExtraAttributes[] = [];
        if (typeof updatedProduct.vendor_products_properties === 'string') {
          JSON.parse(updatedProduct.vendor_products_properties).forEach((p: ProductInterface) =>
            Object.entries(p).forEach(([k, v]) => base.push({ name: k, value: v as string })),
          );
        }
        const allAttrs = [...base, ...attributes];
        updatedProduct.vendor_products_properties = JSON.stringify(allAttrs.map((attr) => ({ [attr.name]: attr.value })));
      }

      // === HELPER FOR % DISCOUNT/RISE ===
      const applyPct = (price: number, pct: number, up: boolean) => Math.round((up ? price * (1 + pct) : price * (1 - pct)) * 100) / 100;

      // === VARIATIONS ===
      if (product.product_variations && product.product_variations.length > 0) {
        // 1) start by applying STOCK to each variation
        let newV = product.product_variations.map((v) => ({
          ...v,
          vendor_product_variations_stock: stock ? parseInt(stock, 10) : v.vendor_product_variations_stock,
        }));

        // 2) override exact “vejledende” price if requested
        if (discountVejlType === 'ændring' && vejlPrice) {
          const val = parseFloat(vejlPrice);
          newV = newV.map((v) => ({ ...v, vendor_product_variations_price: val }));
        }
        // 3) or % change “vejledende”
        if ((discountVejlType === 'stigning' || discountVejlType === 'prisfald') && discountVejl) {
          const pct = parseFloat(discountVejl) / 100;
          const up = discountVejlType === 'stigning';
          newV = newV.map((v) => ({
            ...v,
            vendor_product_variations_price: applyPct(v.vendor_product_variations_price, pct, up),
          }));
        }

        // 4) override exact sales price
        if (discountType === 'ændring' && discountPrice) {
          const val = parseFloat(discountPrice);
          newV = newV.map((v) => ({ ...v, vendor_product_variations_sales_price: val }));
        }
        // 5) or % change sales price
        if ((discountType === 'stigning' || discountType === 'prisfald') && discount) {
          const pct = parseFloat(discount) / 100;
          const up = discountType === 'stigning';
          newV = newV.map((v) => ({
            ...v,
            vendor_product_variations_sales_price:
              v.vendor_product_variations_sales_price != null
                ? applyPct(v.vendor_product_variations_sales_price, pct, up)
                : v.vendor_product_variations_sales_price,
          }));
        }

        // 6) override exact cost price
        if (discountCostType === 'ændring' && costPrice) {
          const val = parseFloat(costPrice);
          newV = newV.map((v) => ({ ...v, vendor_product_variations_cost_price: val }));
        }
        // 7) or % change cost price
        if ((discountCostType === 'stigning' || discountCostType === 'prisfald') && discountCost) {
          const pct = parseFloat(discountCost) / 100;
          const up = discountCostType === 'stigning';
          newV = newV.map((v) => ({
            ...v,
            vendor_product_variations_cost_price:
              v.vendor_product_variations_cost_price != null
                ? applyPct(v.vendor_product_variations_cost_price, pct, up)
                : v.vendor_product_variations_cost_price,
          }));
        }

        updatedProduct.product_variations = newV;
      } else {
        // === SIMPLE (no variations) ===

        // stock
        updatedProduct.vendor_products_stock = stock ? parseInt(stock, 10) : product.vendor_products_stock;

        // exact “vejledende”
        if (discountVejlType === 'ændring' && vejlPrice) {
          updatedProduct.vendor_products_price = parseFloat(vejlPrice);
        }
        // % “vejledende”
        if ((discountVejlType === 'stigning' || discountVejlType === 'prisfald') && discountVejl) {
          const pct = parseFloat(discountVejl) / 100;
          updatedProduct.vendor_products_price = applyPct(product.vendor_products_price, pct, discountVejlType === 'stigning');
        }

        // exact sales
        if (discountType === 'ændring' && discountPrice) {
          updatedProduct.vendor_products_sales_price = parseFloat(discountPrice);
        }
        // % sales
        if ((discountType === 'stigning' || discountType === 'prisfald') && discount) {
          const pct = parseFloat(discount) / 100;
          updatedProduct.vendor_products_sales_price = applyPct(product.vendor_products_sales_price ?? 0, pct, discountType === 'stigning');
        }

        // exact cost
        if (discountCostType === 'ændring' && costPrice) {
          updatedProduct.vendor_products_cost_price = parseFloat(costPrice);
        }
        // % cost
        if ((discountCostType === 'stigning' || discountCostType === 'prisfald') && discountCost) {
          const pct = parseFloat(discountCost) / 100;
          updatedProduct.vendor_products_cost_price = applyPct(product.vendor_products_cost_price ?? 0, pct, discountCostType === 'stigning');
        }
      }

      return updatedProduct;
    });

    // persist back into Dexie
    for (const p of updatedProducts) {
      if (p.vendor_products_id != null) {
        await dexieDB.editedProducts.update(p.vendor_products_id, p);
      }
    }

    setSelectedProducts(updatedProducts);
    setEditedCount(updatedProducts.length);
    setEditedProducts(updatedProducts);
    activateToast();
    clearAll();
  };

  function calculateTotalMarked() {
    let total = 0;

    selectedProducts.forEach((product) => {
      total += 1;

      if (product.product_variations) {
        total += product.product_variations.length;
      }
    });

    return total;
  }

  const removeSelectedProducts = () => {
    selectedProducts.forEach((product) => {
      dexieDB.selectedProducts.delete(product.vendor_products_id);
      dexieDB.editedProducts.delete(product.vendor_products_id);
    });
  };

  const removeProduct = (product: ProductInterface, variation?: ChildVariation) => {
    if (!variation) {
      dexieDB.selectedProducts.delete(product.vendor_products_id);
      dexieDB.editedProducts.delete(product.vendor_products_id);
    }
    if (variation) {
      const updatedVariations = product.product_variations?.filter(
        (v: ChildVariation) => v.vendor_product_variations_id !== variation.vendor_product_variations_id,
      );

      dexieDB.selectedProducts.update(product.vendor_products_id, { product_variations: updatedVariations });
      dexieDB.editedProducts.update(product.vendor_products_id, { product_variations: updatedVariations });
    }
  };

  const handleShowMore = () => {
    setVisibleCount((count) => count + 10);
  };

  const handleShowAll = () => {
    setVisibleCount(editedProductsCount ? editedProductsCount : 0);
  };

  const handleShowMassedit = () => {
    setMassedit((prev) => !prev);
  };

  const getPrevAndNextProduct = (selectedProduct: ProductInterface | null | undefined) => {
    if (!selectedProduct || editedProducts.length === 0) {
      return { prevProduct: null, nextProduct: null };
    }

    const productIndex = editedProducts.findIndex((product) => product.vendor_products_id === selectedProduct.vendor_products_id);

    if (productIndex === -1) {
      return { prevProduct: null, nextProduct: null };
    }

    const prevProduct = productIndex > 0 ? editedProducts[productIndex - 1] : null;
    const nextProduct = productIndex < editedProducts.length - 1 ? editedProducts[productIndex + 1] : null;

    return { prevProduct, nextProduct };
  };

  return (
    <>
      <div className="toast-container">
        <CustomToast
          show={toastActive}
          message={editedCount + (editedCount === 1 ? ' produkt' : ' produkter') + ' blev opdateret.'}
          onClose={() => setToastActive(false)}
          duration={3500}
          title={'Ændringerne er gemt.'}
          type={'toast-success'}
        />
      </div>

      <div className="heading-intro">
        <h1>Produkter</h1>
        <hr className="mt-2" />
        <span>{'Total af ' + calculateTotalMarked() + ' produkter er markeret.'}</span>
      </div>

      <Row className="edit-options">
        <Col xs={4} className="p-0 pe-2">
          <Search placeholder={'Søg efter produkt'} value={searchTerm} onChangeFunction={handleSearchChange} />
        </Col>

        <Col xs={'auto'} className="p-0 pe-2">
          <select
            className="form-select edit-options-category"
            aria-label="Category filter"
            value={category}
            onChange={handleCategoryChange}
            disabled={isLoadingCategories}
          >
            {isLoadingCategories ? <option>Henter Kategorier...</option> : CategoryOptions()}
          </select>
        </Col>
        <Col xs={'auto'} className="p-0">
          <div>
            <Button onClick={handleShowMassedit}>Masseredigering</Button>
          </div>
        </Col>
      </Row>
      {massedit && (
        <div className="mass-edit">
          <Row>
            <div className="mass-edit-header">
              <p>Masseredigering</p>
              <Button className="mass-edit-close" variant="secondary" onClick={handleShowMassedit}>
                <i className="fas fa-xmark" />
              </Button>
            </div>
            <Col>
              <p className="mass-edit-subheader">
                Kategorier
                <button className="mass-edit-subheader-tooltip">
                  <i className="fas fa-regular fa-question-circle" />
                  <span>Vælg de kategorier, produktet skal vises i på hjemmesiden.</span>
                </button>
              </p>
              <div className="mass-edit-search">
                <Search placeholder={'Søg efter kategori'} value={categorySearchTerm} onChangeFunction={handleCategorySearchChange}></Search>
              </div>
              <div className="mass-edit-checkboxes">
                {isLoadingCategories ? (
                  <p>Loading</p>
                ) : selectedProducts && selectedProducts.length ? (
                  categoryView()
                ) : (
                  <p className="mass-edit-checkboxes-no-selected">*Vælg først et produkt</p>
                )}
              </div>
            </Col>
            <Col>
              <p className="mass-edit-subheader">
                Brands
                <button className="mass-edit-subheader-tooltip">
                  <i className="fas fa-regular fa-question-circle" />
                  <span>Vælg de brands, produktet skal vises i på hjemmesiden.</span>
                </button>
              </p>
              <div className="mass-edit-search">
                <Search placeholder={'Søg efter brand'} value={brandSearchTerm} onChangeFunction={handleBrandSearchChange}></Search>
              </div>
              <div className="mass-edit-checkboxes">
                {isLoadingBrands ? (
                  <p>Loading</p>
                ) : selectedProducts && selectedProducts.length ? (
                  brandView()
                ) : (
                  <p className="mass-edit-checkboxes-no-selected">*Vælg først et produkt</p>
                )}
              </div>
            </Col>
            <Col>
              <p className="mass-edit-subheader">
                Varenøgleord
                <button className="mass-edit-subheader-tooltip">
                  <i className="fas fa-regular fa-question-circle" />
                  <span>Vælg de varenøgleord, som skal vises på produktet på hjemmesiden.</span>
                </button>
              </p>
              <div className="mass-edit-search">
                <Search placeholder={'Søg efter varenøgleord'} value={tagSearchTerm} onChangeFunction={handleTagSearchChange}></Search>
              </div>
              <div className="mass-edit-checkboxes">
                {isLoadingTags ? (
                  <p>Loading</p>
                ) : selectedProducts && selectedProducts.length ? (
                  tagView()
                ) : (
                  <p className="mass-edit-checkboxes-no-selected">*Vælg først et produkt</p>
                )}
              </div>
            </Col>
            <Col className="mass-edit-options">
              <Row>
                <p className="mass-edit-subheader invisible">-</p>
                <Col xs={4}>
                  <p className="mass-edit-subheader">
                    Pris
                    <button className="mass-edit-subheader-tooltip">
                      <i className="fas fa-regular fa-question-circle" />
                      <span>
                        Angiv produktets salgspris, som vises til kunden. Sæt én pris for valgte produkter, eller procentvis op/nedjuster priserne for de valgte
                        produkter.
                      </span>
                    </button>
                  </p>
                </Col>
                <Col>
                  <select className="form-select" aria-label="Price change" onChange={(e) => setDiscountVejlType(e.target.value)}>
                    <option value="">- Ingen ændring -</option>
                    <option value="ændring">Skift til:</option>
                    <option value="stigning">Prisstigning %</option>
                    <option value="prisfald">Prisfald %</option>
                  </select>
                  {discountVejlType === 'ændring' ? (
                    <input
                      className="form-control"
                      type="number"
                      min={0}
                      step={50}
                      placeholder={'Vejl. pris'}
                      value={vejlPrice}
                      onChange={(e) => setVejlPrice(e.target.value)}
                    />
                  ) : null}
                  {discountVejlType === 'stigning' || discountVejlType === 'prisfald' ? (
                    <input
                      className="form-control"
                      type="number"
                      min={0}
                      max={100}
                      placeholder={'Indtast ændring i %'}
                      value={discountVejl}
                      onChange={(e) => {
                        Number(e.target.value) <= 100 ? setDiscountVejl(e.target.value) : setDiscountVejl('');
                      }}
                    />
                  ) : null}
                </Col>
              </Row>
              <Row>
                <Col xs={4}>
                  <p className="mass-edit-subheader">
                    Tilbud
                    <button className="mass-edit-subheader-tooltip">
                      <i className="fas fa-regular fa-question-circle" />
                      <span>
                        Indstil en tilbudspris, der vises i stedet for normalprisen i en begrænset periode. Sæt én tilbudspris for valgte produkter, eller
                        procentvis op/nedjuster tilbudspriserne.
                      </span>
                    </button>
                  </p>
                </Col>
                <Col>
                  <select className="form-select" aria-label="Discount change" onChange={(e) => setDiscountType(e.target.value)}>
                    <option value="">- Ingen ændring -</option>
                    <option value="ændring">Skift til:</option>
                    <option value="stigning">Prisstigning %</option>
                    <option value="prisfald">Prisfald %</option>
                  </select>
                  {discountType === 'ændring' ? (
                    <input
                      className="form-control"
                      type="number"
                      min={0}
                      step={50}
                      placeholder={'Tilbudspris'}
                      value={discountPrice}
                      onChange={(e) => setDiscountPrice(e.target.value)}
                    />
                  ) : null}
                  {discountType === 'stigning' || discountType === 'prisfald' ? (
                    <input
                      className="form-control"
                      type="number"
                      min={0}
                      max={100}
                      placeholder={'Indtast ændring i %'}
                      value={discount}
                      onChange={(e) => {
                        Number(e.target.value) <= 100 ? setDiscount(e.target.value) : setDiscount('');
                      }}
                    />
                  ) : null}
                </Col>
              </Row>
              <Row>
                <Col xs={4}>
                  <p className="mass-edit-subheader">
                    Kostpris
                    <button className="mass-edit-subheader-tooltip">
                      <i className="fas fa-regular fa-question-circle" />
                      <span>
                        Indtast indkøbsprisen for at få overblik over produktets profitmargin. Sæt én kostpris for valgte produkter, eller procentvis
                        op/nedjuster kostpriserne.
                      </span>
                    </button>
                  </p>
                </Col>
                <Col>
                  <select className="form-select" aria-label="Cost change" onChange={(e) => setDiscountCostType(e.target.value)}>
                    <option value="">- Ingen ændring -</option>
                    <option value="ændring">Skift til:</option>
                    <option value="stigning">Prisstigning %</option>
                    <option value="prisfald">Prisfald %</option>
                  </select>
                  {discountCostType === 'ændring' ? (
                    <input
                      className="form-control"
                      type="number"
                      min={0}
                      step={50}
                      placeholder={'Kostpris'}
                      value={costPrice}
                      onChange={(e) => setCostPrice(e.target.value)}
                    />
                  ) : null}
                  {discountCostType === 'stigning' || discountCostType === 'prisfald' ? (
                    <input
                      className="form-control"
                      type="number"
                      min={0}
                      max={100}
                      placeholder={'Indtast ændring i %'}
                      value={discountCost}
                      onChange={(e) => {
                        Number(e.target.value) <= 100 ? setDiscountCost(e.target.value) : setDiscountCost('');
                      }}
                    />
                  ) : null}
                </Col>
              </Row>
              <Row>
                <Col xs={4}>
                  <p className="mass-edit-subheader">
                    Status
                    <button className="mass-edit-subheader-tooltip">
                      <i className="fas fa-regular fa-question-circle" />
                      <span>
                        Vælg produktets synlighed på webshoppen. &#39;Kladde&#39; skjuler produktet for kunderne, mens &#39;Udgivet&#39; gør det synligt.
                      </span>
                    </button>
                  </p>
                </Col>
                <Col>
                  <select className="form-select" aria-label="Status change" onChange={(e) => setStatus(e.target.value)}>
                    <option value="">- Ingen ændring -</option>
                    <option value="draft">Kladde</option>
                    <option value="publish">Udgiv</option>
                  </select>
                </Col>
              </Row>
              <Row>
                <Col xs={4}>
                  <p className="mass-edit-subheader">
                    Lagerantal
                    <button className="mass-edit-subheader-tooltip">
                      <i className="fas fa-regular fa-question-circle" />
                      <span>Indtast det aktuelle antal enheder på lager, så lagerstatus vises korrekt for kunden.</span>
                    </button>
                  </p>
                </Col>
                <Col>
                  <input className="form-control" type="number" min={0} placeholder={'Skriv antal'} value={stock} onChange={(e) => setStock(e.target.value)} />
                </Col>
              </Row>
            </Col>
            <Col className="mass-edit-options">
              <p className="mass-edit-subheader">
                Attributter
                <button className="mass-edit-subheader-tooltip">
                  <i className="fas fa-regular fa-question-circle" />
                  <span>
                    Tildel attributter som størrelse, farve eller materiale til produktet. Disse bruges til at vise yderligere detaljer og kan være grundlag for
                    variationer.
                  </span>
                </button>
              </p>
              {attributes.map((attribute, index) => (
                <Row key={index}>
                  <Col>
                    <input
                      className="form-control"
                      id={`attribute-name-${index}`}
                      type="text"
                      placeholder={'Indtast navn'}
                      value={attribute.name}
                      onChange={(e) => handleAttributeChange(index, 'name', e.target.value)}
                    />
                  </Col>
                  <Col>
                    <input
                      className="form-control"
                      id={`attribute-value-${index}`}
                      type="text"
                      placeholder={'Indtast værdi'}
                      value={attribute.value}
                      onChange={(e) => handleAttributeChange(index, 'value', e.target.value)}
                    />
                  </Col>
                  <div className="mass-edit-options-remove">
                    <Button className="mass-edit-options-remove-btn" variant="secondary" onClick={() => handleRemoveAttribute(index)}>
                      <i className="fas fa-xmark" />
                    </Button>
                  </div>
                </Row>
              ))}
              <Button className="mass-edit-options-btn" onClick={handleAddAttribute}>
                Tilføj attribut
              </Button>
            </Col>
          </Row>
          <div className="mt-2">
            <Button className="mass-edit-btn" variant="secondary" onClick={() => clearAll()}>
              Ryd alt
            </Button>
            <Button className="ms-2 mass-edit-btn" onClick={performMassChange} disabled={!selectedProducts || !selectedProducts.length}>
              Anvend
            </Button>
            <Button
              className="ms-2 mass-edit-btn float-end"
              variant="secondary"
              onClick={() => removeSelectedProducts()}
              disabled={selectedProducts.length === 0}
            >
              Fjern produkter
            </Button>
          </div>
        </div>
      )}

      <Table className="mt-4 product-table">
        <thead>
          <tr className="mt-5 mb-4">
            <th className="text-center" onClick={handleMarkAll}>
              <div className="edit-custom-checkbox big-checkbox ms-1">
                <span className="whitespace"></span>
                <label>
                  <input type="checkbox" onClick={(event) => event.stopPropagation()} />
                  <span className="edit-custom-checkbox-check" aria-hidden="true">
                    <i className={(markAll ? '' : 'hidden') + ' fas fa-check'} />
                  </span>
                </label>
              </div>
            </th>

            <th className="d-table-cell" style={{ width: '100px' }}>
              <label>Billede</label>
            </th>

            <th className="d-none d-sm-table-cell">
              <label className="sorting-label" onClick={() => handleSort('vendor_products_sku')}>
                Varenummer
                {sortField === 'vendor_products_sku' && (sortOrder === 'asc' ? <i className="fas fa-sort-up" /> : <i className="fas fa-sort-down" />)}
                {sortField !== 'vendor_products_sku' && <i className="fas fa-sort" />}
              </label>
            </th>

            <th style={{ width: '30%' }}>
              <label className="sorting-label" onClick={() => handleSort('vendor_products_name')}>
                Produktnavn
                {sortField === 'vendor_products_name' && (sortOrder === 'asc' ? <i className="fas fa-sort-up" /> : <i className="fas fa-sort-down" />)}
                {sortField !== 'vendor_products_name' && <i className="fas fa-sort" />}
              </label>
            </th>

            <th className="d-none d-sm-table-cell" style={{ minWidth: '120px' }}>
              <label className="sorting-label" onClick={() => handleSort('vendor_products_category')}>
                Kategori
                {sortField === 'vendor_products_category' && (sortOrder === 'asc' ? <i className="fas fa-sort-up" /> : <i className="fas fa-sort-down" />)}
                {sortField !== 'vendor_products_category' && <i className="fas fa-sort" />}
              </label>
            </th>

            <th className="d-none d-sm-table-cell" style={{ minWidth: '120px' }}>
              <label className="sorting-label" onClick={() => handleSort('vendor_products_brand')}>
                Brand
                {sortField === 'vendor_products_brand' && (sortOrder === 'asc' ? <i className="fas fa-sort-up" /> : <i className="fas fa-sort-down" />)}
                {sortField !== 'vendor_products_brand' && <i className="fas fa-sort" />}
              </label>
            </th>

            <th className="d-none d-lg-table-cell" style={{ minWidth: '120px' }}>
              <label className="sorting-label" onClick={() => handleSort('vendor_products_tags')}>
                Varenøgleord
                {sortField === 'vendor_products_tags' && (sortOrder === 'asc' ? <i className="fas fa-sort-up" /> : <i className="fas fa-sort-down" />)}
                {sortField !== 'vendor_products_tags' && <i className="fas fa-sort" />}
              </label>
            </th>

            <th>
              <label className="sorting-label" onClick={() => handleSort('vendor_products_price')}>
                Pris
                {sortField === 'vendor_products_price' && (sortOrder === 'asc' ? <i className="fas fa-sort-up" /> : <i className="fas fa-sort-down" />)}
                {sortField !== 'vendor_products_price' && <i className="fas fa-sort" />}
              </label>
            </th>

            <th className="d-none d-sm-table-cell">
              <label>Status</label>
            </th>

            <th className="d-none d-xl-table-cell">
              <label>Lager</label>
            </th>

            <th></th>
          </tr>
        </thead>
        <tbody>
          {editedProducts &&
            editedProducts.slice(0, visibleCount).map((product) => {
              if (product.product_variations && product.product_variations.length !== 0) {
                return (
                  <VariableEditProducts
                    key={product.vendor_products_id}
                    product={product}
                    selectedProducts={selectedProducts}
                    setIsSimpleModalOpen={setIsSimpleModalOpen}
                    setProductUnderEdit={setProductUnderEdit}
                    webshopCategories={webshopCategories}
                    webshopTags={webshopTags}
                    webshopBrands={webshopBrands}
                    removeProduct={removeProduct}
                  />
                );
              } else {
                return (
                  <tr
                    className={selectedProducts.some((p) => p.vendor_products_id === product.vendor_products_id) ? 'product-table-selected' : ''}
                    key={product.vendor_products_id}
                  >
                    <SimpleEditProducts
                      key={product.vendor_products_id}
                      product={product}
                      selectedProducts={selectedProducts}
                      setIsSimpleModalOpen={setIsSimpleModalOpen}
                      setProductUnderEdit={setProductUnderEdit}
                      productUnderEdit={productUnderEdit}
                      webshopCategories={webshopCategories}
                      webshopTags={webshopTags}
                      webshopBrands={webshopBrands}
                      removeProduct={removeProduct}
                    />
                  </tr>
                );
              }
            })}
        </tbody>
      </Table>
      <Row className="mb-1 mt-4">
        <Col>
          {visibleCount < (editedProductsCount ? editedProductsCount : 0) && (
            <>
              <Button className="show-more" onClick={handleShowMore} variant="primary">
                Vis mere
              </Button>
              <Button className="mx-2 show-more" onClick={handleShowAll} variant="primary">
                Vis alle ({editedProductsCount})
              </Button>
            </>
          )}
        </Col>
      </Row>

      <SimpleProductModal
        productUnderEdit={productUnderEdit}
        setProductUnderEdit={setProductUnderEdit}
        isSimpleModalOpen={isSimpleModalOpen}
        setIsSimpleModalOpen={setIsSimpleModalOpen}
        webshopCategories={webshopCategories}
        prevAndNext={getPrevAndNextProduct(productUnderEdit)}
        webshopTags={webshopTags}
        webshopBrands={webshopBrands}
      />
    </>
  );
};
